import apiClient from '@shared/services/apiClient';
import type { ClinicListDto, ClinicDetailDto, ClinicServiceFee, ServiceGroupWithProceduresDto, UpdateClinicServicesRequest, UpdateClinicRequest, UpdateClinicOnboardingRequest, CreateClinicRequest, ClinicWithDistance, ClinicCategoryType, ClinicCategoryDto, ClinicCapabilityType } from '../types/clinic';
import type { PaginatedResult } from '@shared/types/api';
import type { VendorFee } from '../types/fee';
import type { Contact } from '../types/contact';
import type { DuplicateCheckResult } from '../types/wizard';
import type { VerificationQueueItem } from '../types/verification';

export interface UpdateOperatingHoursRequest {
  monStart?: string | null;
  monEnd?: string | null;
  tueStart?: string | null;
  tueEnd?: string | null;
  wedStart?: string | null;
  wedEnd?: string | null;
  thurStart?: string | null;
  thurEnd?: string | null;
  friStart?: string | null;
  friEnd?: string | null;
  satStart?: string | null;
  satEnd?: string | null;
  sunStart?: string | null;
  sunEnd?: string | null;
  // IsER / IsAfterHour are no longer sent: the server derives them from the
  // category / capability lists (Clinic Network decision 2026-09-16).
}

// Subset of the server's UpdateClinicCapabilitiesRequest that the UI still
// writes. The five capability flags (HasPAT, HasXRay, FitTestType, IsER,
// IsAfterHour) are derived server-side from the category / capability /
// procedure lists (Clinic Network decision 2026-09-16) and must NOT be sent —
// an omitted flag keeps its derived value. Fields stored in onboarding columns
// (vendor codes, ECoC providers, DOT cert, translation services) do NOT belong
// here either — the capabilities endpoint silently drops unknown fields; send
// those via updateClinicOnboarding instead (SLV2-2999).
export interface UpdateCapabilitiesRequest {
  /** Sub-field of the 'Masks' capability. Omit to clear. */
  maskTypes?: string;
  /** Sub-field of the 'Adapters' capability. Omit to clear. */
  adapterTypes?: string;
}

export interface QuickAddClinicRequest {
  name: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
  phone?: string;
  fax?: string;
  acknowledgedDuplicateWarning: boolean;
}

export const clinicApi = {
  getClinics: async (page = 1, pageSize = 20, search?: string, status?: number): Promise<PaginatedResult<ClinicListDto>> => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (search) params.append('search', search);
    if (status !== undefined) params.append('status', String(status));
    const response = await apiClient.get(`/clinics?${params}`);
    return response.data;
  },

  getClinicById: async (id: number): Promise<ClinicDetailDto> => {
    const response = await apiClient.get(`/clinics/${id}`);
    return response.data;
  },

  getClinicServices: async (id: number): Promise<ClinicServiceFee[]> => {
    const response = await apiClient.get(`/clinics/${id}/services`);
    return response.data;
  },

  getClinicFees: async (id: number): Promise<VendorFee[]> => {
    const response = await apiClient.get(`/clinics/${id}/fees`);
    return response.data;
  },

  getClinicContacts: async (id: number): Promise<Contact[]> => {
    const response = await apiClient.get(`/clinics/${id}/contacts`);
    return response.data;
  },

  checkDuplicate: async (name: string, city: string, state: string, zip: string): Promise<DuplicateCheckResult> => {
    const params = new URLSearchParams({ name, city, state, zip });
    const response = await apiClient.get(`/clinics/check-duplicate?${params}`);
    return response.data;
  },

  verifyClinic: async (
    id: number,
    sessionData?: { stepsConfirmed: number; stepsUpdated: number; stepsSkipped: number; notes?: string }
  ): Promise<{ message: string; verifiedDate: string }> => {
    const response = await apiClient.post(`/clinics/${id}/verify`, sessionData ?? {});
    return response.data;
  },

  updateStatus: async (id: number, status: number): Promise<{ message: string; newStatus: number }> => {
    const response = await apiClient.patch(`/clinics/${id}/status`, { status });
    return response.data;
  },

  updateClinicAddress: async (
    id: number,
    request: {
      addressType: 'Mailing' | 'Shipping';
      street?: string;
      suite?: string;
      city?: string;
      state?: string;
      zip?: string;
      country?: string;
    }
  ): Promise<{ message: string }> => {
    const response = await apiClient.put(`/clinics/${id}/address`, request);
    return response.data;
  },

  copyMailingToShipping: async (id: number): Promise<{ message: string }> => {
    const response = await apiClient.post(`/clinics/${id}/copy-mailing-to-shipping`);
    return response.data;
  },

  updateOperatingHours: async (
    id: number,
    request: UpdateOperatingHoursRequest
  ): Promise<{ message: string }> => {
    const response = await apiClient.put(`/clinics/${id}/operating-hours`, request);
    return response.data;
  },

  getClinicServicesMaster: async (id: number): Promise<ServiceGroupWithProceduresDto[]> => {
    const response = await apiClient.get(`/clinics/${id}/services/master`);
    return response.data;
  },

  updateClinicServices: async (id: number, request: UpdateClinicServicesRequest): Promise<{ message: string }> => {
    const response = await apiClient.put(`/clinics/${id}/services`, request);
    return response.data;
  },

  updateCapabilities: async (id: number, request: UpdateCapabilitiesRequest): Promise<{ message: string }> => {
    const response = await apiClient.put(`/clinics/${id}/capabilities`, request);
    return response.data;
  },

  updateClinic: async (id: number, request: UpdateClinicRequest): Promise<{ message: string }> => {
    const response = await apiClient.put(`/clinics/${id}`, request);
    return response.data;
  },

  // The only write path for onboarding columns (vendor codes, ECoC providers,
  // DOT cert, translation services). Other update endpoints silently ignore
  // these fields — do not send them anywhere else and expect persistence.
  updateClinicOnboarding: async (id: number, request: UpdateClinicOnboardingRequest): Promise<{ message: string }> => {
    const response = await apiClient.put(`/clinics/${id}/onboarding`, request);
    return response.data;
  },

  createClinic: async (request: CreateClinicRequest): Promise<{ id: number }> => {
    const response = await apiClient.post('/clinics', request);
    return response.data;
  },

  quickAddClinic: async (request: QuickAddClinicRequest): Promise<{ id: number; taskId: number | null }> => {
    const response = await apiClient.post('/clinics/quick-add', request);
    return response.data;
  },

  getVerificationQueue: async (
    page = 1,
    pageSize = 20,
    search?: string,
    sortColumn?: string,
    sortDirection?: string,
    includeInactive?: boolean,
  ): Promise<PaginatedResult<VerificationQueueItem>> => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (search) params.append('search', search);
    if (sortColumn) params.append('sortColumn', sortColumn);
    if (sortDirection) params.append('sortDirection', sortDirection);
    if (includeInactive) params.append('includeInactive', 'true');
    const response = await apiClient.get(`/clinics/verification-queue?${params}`);
    return response.data;
  },

  getVerificationQueueCount: async (): Promise<{ count: number }> => {
    const response = await apiClient.get('/clinics/verification-queue/count');
    return response.data;
  },

  geocodeAddress: async (address: string): Promise<{ latitude: number; longitude: number }> => {
    const params = new URLSearchParams({ address });
    const response = await apiClient.get(`/clinics/geocode-address?${params}`);
    return response.data;
  },

  getNearbyClinics: async (
    latitude: number,
    longitude: number,
    radiusMiles: number = 25,
    limit: number = 50
  ): Promise<ClinicWithDistance[]> => {
    const params = new URLSearchParams({
      latitude: String(latitude),
      longitude: String(longitude),
      radiusMiles: String(radiusMiles),
      limit: String(limit),
    });
    const response = await apiClient.get(`/clinics/nearby?${params}`);
    return response.data;
  },

  // Clinics by explicit id set (Clinic Matrix related clinics) — same shape as
  // getNearbyClinics, but not limited by radius/limit; ungeocoded clinics come
  // back with null coordinates/distance.
  getClinicsByIds: async (
    ids: number[],
    latitude?: number,
    longitude?: number
  ): Promise<ClinicWithDistance[]> => {
    const params = new URLSearchParams({ ids: ids.join(',') });
    if (latitude != null && longitude != null) {
      params.set('latitude', String(latitude));
      params.set('longitude', String(longitude));
    }
    const response = await apiClient.get(`/clinics/by-ids?${params}`);
    return response.data;
  },

  // Category endpoints
  getCategoryTypes: async (): Promise<ClinicCategoryType[]> => {
    const response = await apiClient.get('/clinics/category-types');
    return response.data;
  },

  getClinicCategories: async (id: number): Promise<ClinicCategoryDto[]> => {
    const response = await apiClient.get(`/clinics/${id}/categories`);
    return response.data;
  },

  updateCategories: async (id: number, categoryTypeIds: number[]): Promise<{ message: string }> => {
    const response = await apiClient.put(`/clinics/${id}/categories`, { categoryTypeIds });
    return response.data;
  },

  // Capability type endpoints
  getCapabilityTypes: async (): Promise<ClinicCapabilityType[]> => {
    const response = await apiClient.get('/clinics/capability-types');
    return response.data;
  },

  getClinicCapabilityList: async (id: number): Promise<number[]> => {
    const response = await apiClient.get(`/clinics/${id}/capability-list`);
    return response.data;
  },

  updateCapabilityList: async (id: number, capabilityTypeIds: number[]): Promise<{ message: string }> => {
    const response = await apiClient.put(`/clinics/${id}/capability-list`, { capabilityTypeIds });
    return response.data;
  },
};
